<template>
  <div class="animated fadeIn page-container">
    <h1>Dry Dock Monitoring</h1>

    <!-- new filter panel -->
    <filter-panel
      class="filters-panel"
      :filter-parameters="computedFilterParameters"
      @update="applyFilterHandler"
      :notification-text="'This dashboard currently only includes dry dock visits in Japan/Korea/China'"
      :api-request-data="apiRequestData"/>

    <!-- dry dock stats charts -->
    <b-row>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Capesize (vessels in dry dock)"
          :dates="drydockStats.capesize.date"
          :values="drydockStats.capesize.value"
          :values-format="drydockStatsValuesFormat"
          :y-axis="{titleText: this.yAxisTitleText}"
          :export-types="exportTypes"
          :export-loading="exportLoading.capesize"
          @export="handleExport('capesize', $event)"/>
      </b-col>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Panamax (vessels in dry dock)"
          :dates="drydockStats.panamax.date"
          :values="drydockStats.panamax.value"
          :values-format="drydockStatsValuesFormat"
          :y-axis="{titleText: this.yAxisTitleText}"
          :export-types="exportTypes"
          :export-loading="exportLoading.panamax"
          @export="handleExport('panamax', $event)"/>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Supramax (vessels in dry dock)"
          :dates="drydockStats.supramax.date"
          :values="drydockStats.supramax.value"
          :values-format="drydockStatsValuesFormat"
          :y-axis="{titleText: this.yAxisTitleText}"
          :export-types="exportTypes"
          :export-loading="exportLoading.supramax"
          @export="handleExport('supramax', $event)"/>
      </b-col>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Handysize (vessels in dry dock)"
          :dates="drydockStats.handysize.date"
          :values="drydockStats.handysize.value"
          :values-format="drydockStatsValuesFormat"
          :y-axis="{titleText: this.yAxisTitleText}"
          :export-types="exportTypes"
          :export-loading="exportLoading.handysize"
          @export="handleExport('handysize', $event)"/>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import debounce from 'lodash-es/debounce';
import getNested from 'lodash-es/get';

import TimeseriesChart from '../components/widgets/TimeseriesChart';
import moment from 'moment';
import { downloadAsFileByURL } from '@/shared/download-utils';
import FilterPanel from '../components/widgets/FilterPanel';
import {
  getCurrentAPIFilterValues,
  getAPIFilterValues,
  applyFilterValues,
  getFormattedFilters
} from '../components/widgets/FilterPanel';


export default {
  name: 'drydock',

  components: {
    TimeseriesChart,
    FilterPanel,
  },

  data() {
    const segments = [
      'capesize',
      'panamax',
      'supramax',
      'handysize',
    ];
    let drydockStats = {};
    let exportLoading = {};

    segments.forEach((segmentName) => {
      drydockStats[segmentName] = {};
      exportLoading[segmentName] = false;
    });

    return {
      apiRequestData: {},

      // filter parameters for internal usage
      // and for the <filter-panel> props (specific format required)
      filterSchema: {
        metric: {
          name: 'metric',
          apiName: 'metric',
          type: 'multiselect',
          placeholder: 'Data type',
          value: {value: 'vessel_count', text: 'Vessel count'},
          choices: [
            {value: 'vessel_count', text: 'Vessel count'},
            {value: 'dwt', text: 'DWT'},
          ],
          options: {
            multiple: false,
            closeOnSelect: true,
          },
        },
        absolute: {
          name: 'absolute',
          apiName: 'absolute',
          type: 'multiselect',
          placeholder: 'Display values as',
          value: {value: true, text: 'Absolute numbers'},
          choices: [
            {value: true, text: 'Absolute numbers'},
            {value: false, text: '% of fleet'},
          ],
          options: {
            multiple: false,
            closeOnSelect: true,
          },
        },
      },

      drydockStats: drydockStats,  // {capesize: {}, ... }
      exportTypes: ['csv'],
      exportLoading: exportLoading,
    };
  },

  methods: {
    ...mapActions('api/drydock', [
      'getDrydockStats',
    ]),

    applyFilterHandler: debounce(
      function (filterValues) {
        this.applyFilter(filterValues);
      },
      200
    ),

    async applyFilter(filterValues) {
      applyFilterValues(this.filterSchema, filterValues);

      for(let segmentName of Object.keys(this.drydockStats)) {
        let filterParameters = {
          ...getAPIFilterValues(this.filterSchema, filterValues),
          segment: [segmentName],
        };

        // all requests will be performed simultaneously
        // since we don't wait for a response with 'then'

        this.getDrydockStats(filterParameters).then(response => {
          this.drydockStats[segmentName] = {date: [], value: []};

          getNested(response, 'data.drydockValues', []).forEach(item => {
            this.drydockStats[segmentName].date.push(item.date || '2001-01-01');
            this.drydockStats[segmentName].value.push(item.value || 0);
          });

          this.apiRequestData = {drydockStats: response.requestData};
        });
      }
    },

    async handleExport(segment, format) {
      this.exportLoading[segment] = true;

      const filterParameters = {
        ...getCurrentAPIFilterValues(this.filterSchema),
        segment: [segment],
        format,
      };

      const response = await this.getDrydockStats(filterParameters);
      const timestamp = moment().format('YYYY-MM-DD-HH-mm');

      downloadAsFileByURL(
        getNested(response, 'data.csv', null),
        `drydock_${filterParameters.segment}_export_${timestamp}.${format}`
      );
      this.exportLoading[segment] = false;
    },
  },

  computed: {
    ...mapGetters(
      'api',
      ['getActiveRequestsCount',]
    ),

    ...mapGetters(
      'api/presets', ['getActivePreset']
    ),

    computedFilterParameters() {
      return getFormattedFilters(this.filterSchema);
    },

    drydockStatsValuesFormat: function () {
      // non absolute values are percent
      return getNested(this.filterSchema, 'absolute.value.value') ? undefined : 'percent';
    },

    yAxisTitleText: function () {
      if(this.filterSchema.metric.value === 'vesselCount') {
        return this.filterSchema.absolute.value ? '# vessels' : '% of fleet (# of vessels)';
      } else {
        return this.filterSchema.absolute.value ? 'million DWT' : '% of fleet (DWT)';
      }
    },
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        // if(!this.filtersReady) {
        //   await this.loadFilters();
        //   if(!preset) {
        //     this.setDefaultValues();
        //   }
        // }

        if(preset) {
          const filterValues = getNested(preset, 'params.filterPanel', {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      },
    },
  },
};
</script>

<style scoped>
.page-container >>> .card-content {
  min-height: 460px;
}

.filters-panel >>> .filter-field__select {
  min-width: 300px;
}

.filters-panel >>> .filter-field__select select {
  min-width: 150px;
}

</style>

<style scoped lang='scss'>

</style>
